import {createAction, ActionCreator} from '@packages/utilities/create-action';
import {Plugins, Component} from '@packages/systems/wfdl';
import {
  LIBRARIES_FETCH_REQUESTED,
  LIBRARY_INSTALLATIONS_FETCH_REQUESTED,
  MANAGE_LIBRARY_INSTALL_STARTED,
  MANAGE_LIBRARY_INSTALL_SUCCEEDED,
  MANAGE_LIBRARY_INSTALL_FAILED,
  MANAGE_LIBRARY_UNINSTALL_STARTED,
  MANAGE_LIBRARY_UNINSTALL_SUCCEEDED,
  MANAGE_LIBRARY_UNINSTALL_FAILED,
  PUBLISH_SITE_LIBRARY_REQUESTED,
  UNPUBLISH_SITE_LIBRARY_STARTED,
  DYNAMIC_INSTALL_READY,
  DYNAMIC_INSTALL_FAILED,
  DYNAMIC_INSTALL_FINISHED,
  DYNAMIC_PLUGINS_RELOAD_STARTED,
  DYNAMIC_PLUGINS_RELOAD_FINISHED,
  DYNAMIC_PLUGINS_RELOAD_FAILED,
  DYNAMIC_PLUGINS_READY,
  DETACH_LIBRARY_COMPONENT_INSTANCE,
  UNLINK_LIBRARY_COMPONENT_INSTANCE,
  LIBRARY_ASSET_IMPORT_SUCCEEDED,
  PUSH_LIBRARY_UPDATES_REQUESTED,
  PUSH_LIBRARY_UPDATES_SUCCEEDED,
  PUSH_LIBRARY_UPDATES_FAILED,
  LIBRARY_CHANGELOG_STARTED,
  LIBRARY_CHANGELOG_FAILED,
  LIBRARY_CHANGELOG_FINISHED,
  LIBRARY_CHANGELOG_RESET,
  LIBRARY_MODAL_OPENED,
  LIBRARY_MODAL_CLOSED,
  LIBRARY_TO_INSTALL_AFTER_DISABLE_CLONING_SET,
  INSTALLED_RESOURCES_LOADED,
  RECOVER_ORPHANED_LIBRARY_CONTENT_MODAL_OPENED,
  RECOVER_ORPHANED_LIBRARY_CONTENT_MODAL_CLOSED,
  RECOVER_ORPHANED_LIBRARY_CONTENT_ENGINE_BOOTSTRAPPED,
  RECOVER_ORPHANED_LIBRARY_CONTENT_ENGINE_DATA_UPDATED,
  RECOVER_ORPHANED_LIBRARY_CONTENT_ENGINE_STARTED,
  RECOVER_ORPHANED_LIBRARY_CONTENT_ENGINE_FINISHED,
  RECOVER_ORPHANED_LIBRARY_CONTENT_ENGINE_FAILED,
  RECOVER_ORPHANED_LIBRARY_CONTENT_ENQUEUE_STARTED,
  RECOVER_ORPHANED_LIBRARY_CONTENT_ENQUEUE_SUCCEEDED,
  RECOVER_ORPHANED_LIBRARY_CONTENT_JOB_FAILED,
  RECOVER_ORPHANED_LIBRARY_CONTENT_JOB_SUCCEEDED,
  RECOVER_ORPHANED_LIBRARY_CONTENT_RELOAD_STARTED,
  RECOVER_ORPHANED_LIBRARY_CONTENT_RELOAD_FAILED,
  RECOVER_ORPHANED_LIBRARY_CONTENT_RELOAD_FINISHED,
  LIBRARY_ASSET_ENGINE_STARTED,
  LIBRARY_ASSET_ENGINE_RUNNING,
  LIBRARY_ASSET_ENGINE_FAILED,
  LIBRARY_ASSET_ENGINE_FINISHED,
  LIBRARY_ASSET_DUPLICATED,
  LIBRARY_ASSET_SETTINGS_UPDATED,
  LIBRARIES_UPDATES_AVAILABLE,
  LIBRARY_PUSH_UPDATES_AVAILABLE,
  LIBRARY_TAG_STYLES_SETTINGS_CHANGE_REQUESTED,
  INSTALLED_LIBRARIES_TAG_STYLES_SETTINGS_UPDATED,
  LIBRARY_COMPONENT_CREATED,
} from './actionTypes';
import type {
  DiffResultType,
  LibraryAssetManagementMap,
  LibraryExport,
  LibraryVersionedResourceResult,
  VerifiedLibraryExport,
  LibraryModalTypeData,
  LibraryModalType,
  LIBRARY_ACTION,
  LibrarySettings,
  LibraryManifest,
  LibrariesInstallationType,
  LibraryTagStylesSettings,
  InstalledLibraryTagStylesSettings,
} from '@packages/systems/libraries';
import {Qualified} from '@packages/systems/wfdl-Qualified';
import {Map} from 'immutable';
import type {DesignerAction} from '@packages/systems/designer/flux';
import {RawFileType} from '@packages/systems/core/records/FileRecord';
import type {TaskStatusPayload} from '@packages/systems/tasks';
import {
  LibraryAssetsManager,
  LibraryOrphanedComponent,
  LibraryOrphanedVariable,
} from '../types';

export const fetchAvailableLibrariesRequested: ActionCreator<
  typeof LIBRARIES_FETCH_REQUESTED,
  void
> = createAction(LIBRARIES_FETCH_REQUESTED);

export const fetchLibraryInstallationsRequested: ActionCreator<
  typeof LIBRARY_INSTALLATIONS_FETCH_REQUESTED,
  {
    profileId: string; // SharedResourceProfile ID
  }
> = createAction(LIBRARY_INSTALLATIONS_FETCH_REQUESTED);

export const recoverOrphanedLibraryContentModalOpened: ActionCreator<
  typeof RECOVER_ORPHANED_LIBRARY_CONTENT_MODAL_OPENED,
  void
> = createAction(RECOVER_ORPHANED_LIBRARY_CONTENT_MODAL_OPENED);

export const recoverOrphanedLibraryContentModalClosed: ActionCreator<
  typeof RECOVER_ORPHANED_LIBRARY_CONTENT_MODAL_CLOSED,
  void
> = createAction(RECOVER_ORPHANED_LIBRARY_CONTENT_MODAL_CLOSED);

export const recoverOrphanedLibraryContentEngineBootstrapped: ActionCreator<
  typeof RECOVER_ORPHANED_LIBRARY_CONTENT_ENGINE_BOOTSTRAPPED,
  void
> = createAction(RECOVER_ORPHANED_LIBRARY_CONTENT_ENGINE_BOOTSTRAPPED);

export const recoverOrphanedLibraryContentEngineDataUpdated: ActionCreator<
  typeof RECOVER_ORPHANED_LIBRARY_CONTENT_ENGINE_DATA_UPDATED,
  {
    key: string;
    orphanedComponents: LibraryOrphanedComponent[];
    orphanedVariables: LibraryOrphanedVariable[];
  }
> = createAction(RECOVER_ORPHANED_LIBRARY_CONTENT_ENGINE_DATA_UPDATED);

export const recoverOrphanedLibraryContentEngineStarted: ActionCreator<
  typeof RECOVER_ORPHANED_LIBRARY_CONTENT_ENGINE_STARTED,
  void
> = createAction(RECOVER_ORPHANED_LIBRARY_CONTENT_ENGINE_STARTED);

export const recoverOrphanedLibraryContentEngineFinished: ActionCreator<
  typeof RECOVER_ORPHANED_LIBRARY_CONTENT_ENGINE_FINISHED,
  {
    key: string;
    updatedComponentsCount: number;
    detachedComponentsCount: number;
  }
> = createAction(RECOVER_ORPHANED_LIBRARY_CONTENT_ENGINE_FINISHED);

export const recoverOrphanedLibraryContentEngineFailed: ActionCreator<
  typeof RECOVER_ORPHANED_LIBRARY_CONTENT_ENGINE_FAILED,
  void
> = createAction(RECOVER_ORPHANED_LIBRARY_CONTENT_ENGINE_FAILED);

export const recoverOrphanedLibraryContentEnqueueStarted: ActionCreator<
  typeof RECOVER_ORPHANED_LIBRARY_CONTENT_ENQUEUE_STARTED,
  {branchId: string | null}
> = createAction(RECOVER_ORPHANED_LIBRARY_CONTENT_ENQUEUE_STARTED);

export const recoverOrphanedLibraryContentEnqueueSucceeded: ActionCreator<
  typeof RECOVER_ORPHANED_LIBRARY_CONTENT_ENQUEUE_SUCCEEDED,
  {
    taskId: string;
  }
> = createAction(RECOVER_ORPHANED_LIBRARY_CONTENT_ENQUEUE_SUCCEEDED);

export const recoverOrphanedLibraryContentJobFailed: ActionCreator<
  typeof RECOVER_ORPHANED_LIBRARY_CONTENT_JOB_FAILED,
  {
    status: TaskStatusPayload<{
      error: unknown;
      snapshot?: {id: string; desc: string};
    }>;
  }
> = createAction(RECOVER_ORPHANED_LIBRARY_CONTENT_JOB_FAILED);

export const recoverOrphanedLibraryContentJobSucceeded: ActionCreator<
  typeof RECOVER_ORPHANED_LIBRARY_CONTENT_JOB_SUCCEEDED,
  {
    status: TaskStatusPayload<{
      updatedComponentsCount: number;
      detachedComponentsCount: number;
    }>;
  }
> = createAction(RECOVER_ORPHANED_LIBRARY_CONTENT_JOB_SUCCEEDED);

export const recoverOrphanedLibraryContentReloadStarted: ActionCreator<
  typeof RECOVER_ORPHANED_LIBRARY_CONTENT_RELOAD_STARTED,
  void
> = createAction(RECOVER_ORPHANED_LIBRARY_CONTENT_RELOAD_STARTED);

export const recoverOrphanedLibraryContentReloadFailed: ActionCreator<
  typeof RECOVER_ORPHANED_LIBRARY_CONTENT_RELOAD_FAILED,
  {error: unknown}
> = createAction(RECOVER_ORPHANED_LIBRARY_CONTENT_RELOAD_FAILED);

export const recoverOrphanedLibraryContentReloadFinished: ActionCreator<
  typeof RECOVER_ORPHANED_LIBRARY_CONTENT_RELOAD_FINISHED,
  void
> = createAction(RECOVER_ORPHANED_LIBRARY_CONTENT_RELOAD_FINISHED);

export const libraryAssetEngineStarted: ActionCreator<
  typeof LIBRARY_ASSET_ENGINE_STARTED,
  Required<Pick<LibraryAssetsManager, 'assetsToImport'>>
> = createAction(LIBRARY_ASSET_ENGINE_STARTED);

export const libraryAssetEngineRunning: ActionCreator<
  typeof LIBRARY_ASSET_ENGINE_RUNNING,
  void
> = createAction(LIBRARY_ASSET_ENGINE_RUNNING);

export const libraryAssetEngineFailed: ActionCreator<
  typeof LIBRARY_ASSET_ENGINE_FAILED,
  void
> = createAction(LIBRARY_ASSET_ENGINE_FAILED);

export const libraryAssetEngineFinished: ActionCreator<
  typeof LIBRARY_ASSET_ENGINE_FINISHED,
  {
    importedAssets: {[key: string]: RawFileType};
  }
> = createAction(LIBRARY_ASSET_ENGINE_FINISHED);

export const libraryComponentCreated: ActionCreator<
  typeof LIBRARY_COMPONENT_CREATED,
  {
    origin: Qualified<Component.ComponentName>;
  }
> = createAction(LIBRARY_COMPONENT_CREATED);

export const libraryInstallStarted: ActionCreator<
  typeof MANAGE_LIBRARY_INSTALL_STARTED,
  {
    profileId: string; // SharedResourceProfile ID
    versionId: string; // VersionedResource ID being installed
    actionType: LIBRARY_ACTION.INSTALL | LIBRARY_ACTION.PULL_UPDATE;
    shouldActivateTagStyles?: boolean;
    metadata?: {
      componentsToDetach?: string[];
    };
  }
> = createAction(MANAGE_LIBRARY_INSTALL_STARTED);

export const libraryToInstallAfterDisableCloningSet: ActionCreator<
  typeof LIBRARY_TO_INSTALL_AFTER_DISABLE_CLONING_SET,
  {
    library: LibraryManifest<string>;
  }
> = createAction(LIBRARY_TO_INSTALL_AFTER_DISABLE_CLONING_SET);

export const libraryUninstallStarted: ActionCreator<
  typeof MANAGE_LIBRARY_UNINSTALL_STARTED,
  {
    profileId: string; // SharedResourceProfile ID
    versionId: string; // VersionedResource ID being uninstalled
  }
> = createAction(MANAGE_LIBRARY_UNINSTALL_STARTED);

export const manageLibraryInstallSucceeded: ActionCreator<
  typeof MANAGE_LIBRARY_INSTALL_SUCCEEDED,
  {
    status: TaskStatusPayload<{
      installedLibraryId?: string;
    }>;
  }
> = createAction(MANAGE_LIBRARY_INSTALL_SUCCEEDED);

export const manageLibraryInstallFailed: ActionCreator<
  typeof MANAGE_LIBRARY_INSTALL_FAILED,
  {
    status: TaskStatusPayload;
  }
> = createAction(MANAGE_LIBRARY_INSTALL_FAILED);

export const manageLibraryUninstallSucceeded: ActionCreator<
  typeof MANAGE_LIBRARY_UNINSTALL_SUCCEEDED,
  {
    status: TaskStatusPayload;
  }
> = createAction(MANAGE_LIBRARY_UNINSTALL_SUCCEEDED);

export const manageLibraryUninstallFailed: ActionCreator<
  typeof MANAGE_LIBRARY_UNINSTALL_FAILED,
  {
    status: string;
  }
> = createAction(MANAGE_LIBRARY_UNINSTALL_FAILED);

export const libraryPublishRequested: ActionCreator<
  typeof PUBLISH_SITE_LIBRARY_REQUESTED,
  {
    name: string;
    description: string;
    libraryExports: LibraryExport<string>[];
    librarySettings?: LibrarySettings;
  }
> = createAction(PUBLISH_SITE_LIBRARY_REQUESTED);
export const libraryUnpublishStarted: ActionCreator<
  typeof UNPUBLISH_SITE_LIBRARY_STARTED,
  {
    profileId: string;
  }
> = createAction(UNPUBLISH_SITE_LIBRARY_STARTED);

export const pushLibraryUpdatesRequested: ActionCreator<
  typeof PUSH_LIBRARY_UPDATES_REQUESTED,
  {
    siteIdsToInstall: string[];
    totalSitesNeedingUpdate: number;
  }
> = createAction(PUSH_LIBRARY_UPDATES_REQUESTED);

export const pushLibraryUpdatesSucceeded: ActionCreator<
  typeof PUSH_LIBRARY_UPDATES_SUCCEEDED,
  void
> = createAction(PUSH_LIBRARY_UPDATES_SUCCEEDED);

export const pushLibraryUpdatesFailed: ActionCreator<
  typeof PUSH_LIBRARY_UPDATES_FAILED,
  void
> = createAction(PUSH_LIBRARY_UPDATES_FAILED);

export const libraryChangelogStarted: ActionCreator<
  typeof LIBRARY_CHANGELOG_STARTED,
  {updatedAt: Date}
> = createAction(LIBRARY_CHANGELOG_STARTED);

export const libraryChangelogFinished: ActionCreator<
  typeof LIBRARY_CHANGELOG_FINISHED,
  {
    priorLibraryResourceId: string | null;
    updatedLibraryResourceId: string | null;
    priorLibraryExports: VerifiedLibraryExport<string>[];
    updatedLibraryExports: VerifiedLibraryExport<string>[];
    diffResult: DiffResultType;
  }
> = createAction(LIBRARY_CHANGELOG_FINISHED);

export const libraryChangelogFailed: ActionCreator<
  typeof LIBRARY_CHANGELOG_FAILED,
  void
> = createAction(LIBRARY_CHANGELOG_FAILED);

export const libraryChangelogReset: ActionCreator<
  typeof LIBRARY_CHANGELOG_RESET,
  void
> = createAction(LIBRARY_CHANGELOG_RESET);

export const libraryAssetSettingsUpdated: ActionCreator<
  typeof LIBRARY_ASSET_SETTINGS_UPDATED,
  {
    assetFoldersIgnored?: string[];
  }
> = createAction(LIBRARY_ASSET_SETTINGS_UPDATED);

export const dynamicInstallReady: ActionCreator<
  typeof DYNAMIC_INSTALL_READY,
  {
    plugins: Plugins.PluginDeclarationMap;
    needsReboot: boolean;
  }
> = createAction(DYNAMIC_INSTALL_READY);

type OpenLibraryModalPayload<T extends LibraryModalType = LibraryModalType> =
  T extends keyof LibraryModalTypeData
    ? {
        modal: T;
        modalData: LibraryModalTypeData[T];
      }
    : {modal: T};

export const openLibraryModal: ActionCreator<
  typeof LIBRARY_MODAL_OPENED,
  OpenLibraryModalPayload
> = createAction(LIBRARY_MODAL_OPENED);

export const closeLibraryModal: ActionCreator<
  typeof LIBRARY_MODAL_CLOSED,
  void
> = createAction(LIBRARY_MODAL_CLOSED);

export const dynamicInstallFailed: ActionCreator<
  typeof DYNAMIC_INSTALL_FAILED,
  {
    plugins: {[pluginId: string]: LibraryVersionedResourceResult['value']};
  }
> = createAction(DYNAMIC_INSTALL_FAILED);

export type LibraryAssetImportMeta = {
  addedComponents?: Qualified<Component.ComponentName>[];
  detachedComponents?: Qualified<Component.ComponentName>[];
  idMap?: {[id: string]: string};
  assetMap?: LibraryAssetManagementMap;
};

export type LibraryAssetImportSucceeded = DesignerAction<
  typeof LIBRARY_ASSET_IMPORT_SUCCEEDED,
  {
    response: {
      importedAssets: {[key: string]: RawFileType};
      updatedAssets: {[key: string]: RawFileType};
      deletedAssets: {[key: string]: RawFileType};
    };
    meta: Map<string, LibraryAssetImportMeta>;
  }
>;

export const dynamicInstallFinished: ActionCreator<
  typeof DYNAMIC_INSTALL_FINISHED,
  void
> = createAction(DYNAMIC_INSTALL_FINISHED);

export const dynamicPluginsReady: ActionCreator<
  typeof DYNAMIC_PLUGINS_READY,
  void
> = createAction(DYNAMIC_PLUGINS_READY);

export const dynamicPluginsReloadStarted: ActionCreator<
  typeof DYNAMIC_PLUGINS_RELOAD_STARTED,
  void
> = createAction(DYNAMIC_PLUGINS_RELOAD_STARTED);

export const dynamicPluginsReloadFinished: ActionCreator<
  typeof DYNAMIC_PLUGINS_RELOAD_FINISHED,
  {
    actionType?:
      | LIBRARY_ACTION.INSTALL
      | LIBRARY_ACTION.PULL_UPDATE
      | LIBRARY_ACTION.UNINSTALL;
  }
> = createAction(DYNAMIC_PLUGINS_RELOAD_FINISHED);

export const dynamicPluginsReloadFailed: ActionCreator<
  typeof DYNAMIC_PLUGINS_RELOAD_FAILED,
  {
    actionType?:
      | LIBRARY_ACTION.INSTALL
      | LIBRARY_ACTION.PULL_UPDATE
      | LIBRARY_ACTION.UNINSTALL;
    error?: unknown;
  }
> = createAction(DYNAMIC_PLUGINS_RELOAD_FAILED);

export const detachLibraryComponentInstance: ActionCreator<
  typeof DETACH_LIBRARY_COMPONENT_INSTANCE,
  {location: 'right-click-menu' | 'keyboard-shortcut'}
> = createAction(DETACH_LIBRARY_COMPONENT_INSTANCE);

export const unlinkLibraryComponentInstance: ActionCreator<
  typeof UNLINK_LIBRARY_COMPONENT_INSTANCE,
  {location: 'right-click-menu' | 'keyboard-shortcut'}
> = createAction(UNLINK_LIBRARY_COMPONENT_INSTANCE);

export const installedResourcesLoaded: ActionCreator<
  typeof INSTALLED_RESOURCES_LOADED,
  {
    installations: LibrariesInstallationType;
    resources: LibraryVersionedResourceResult[];
    reboot: boolean;
  }
> = createAction(INSTALLED_RESOURCES_LOADED);

export const libraryAssetDuplicated: ActionCreator<
  typeof LIBRARY_ASSET_DUPLICATED,
  {
    assetId: string;
  }
> = createAction(LIBRARY_ASSET_DUPLICATED);

export const librariesUpdatesAvailable: ActionCreator<
  typeof LIBRARIES_UPDATES_AVAILABLE,
  {
    libraries: LibraryManifest<string>[];
  }
> = createAction(LIBRARIES_UPDATES_AVAILABLE);

export const libraryPushUpdatesAvailable: ActionCreator<
  typeof LIBRARY_PUSH_UPDATES_AVAILABLE,
  {
    libraries: LibraryManifest<string>[];
    /**
     * Map of library profile IDs to their previous version info.
     * Used to detect library push updates between sessions.
     */
    prevVersions: {
      [profileId: string]: {version: string; pluginId: string};
    };
  }
> = createAction(LIBRARY_PUSH_UPDATES_AVAILABLE);

export const libraryTagStylesSettingsChangeRequested: ActionCreator<
  typeof LIBRARY_TAG_STYLES_SETTINGS_CHANGE_REQUESTED,
  {
    // The whole array in its new order — position is priority and `isActive` is
    // whether the styles apply, so a toggle and a drag both send one of these.
    // The panel builds it from what it rendered; see the endpoint's schema.
    libraryTagStylesSettings: LibraryTagStylesSettings[];
  }
> = createAction(LIBRARY_TAG_STYLES_SETTINGS_CHANGE_REQUESTED);

export const installedLibrariesTagStylesSettingsUpdated: ActionCreator<
  typeof INSTALLED_LIBRARIES_TAG_STYLES_SETTINGS_UPDATED,
  {
    installedLibrariesTagStylesSettings: InstalledLibraryTagStylesSettings;
  }
> = createAction(INSTALLED_LIBRARIES_TAG_STYLES_SETTINGS_UPDATED);
